// Shared calculations for the storefront and the catalog/quote sync scripts.
export function quoteKey(size, lid, color, quantity) {
  return [size, lid, lid === 'none' ? 'none' : color, Number(quantity)].join('|');
}

export function normalizeSelection(config, requested = {}) {
  const sizes = config.sizes.filter((size) => config.quotes.some((quote) => quote.size === size.id));
  const size = sizes.find((item) => item.id === requested.size) || sizes[0];
  if (!size) return null;
  const lids = config.lids.filter((lid) => config.quotes.some((quote) => quote.size === size.id && quote.lid === lid.id));
  const lid = lids.find((item) => item.id === requested.lid) || lids[0];
  const colors = lid.id === 'none' ? [] : config.colors.filter((color) => config.quotes.some((quote) => quote.size === size.id && quote.lid === lid.id && quote.color === color.id));
  const color = lid.id === 'none' ? 'none' : (colors.find((item) => item.id === requested.color) || colors[0])?.id;
  const quotes = config.quotes.filter((quote) => quote.size === size.id && quote.lid === lid.id && quote.color === color).sort((a, b) => a.quantity - b.quantity);
  const quote = quotes.find((item) => item.quantity === Number(requested.quantity)) || quotes[0];
  return { size: size.id, lid: lid.id, color, quantity: quote.quantity, quote, quotes, sizes, lids, colors };
}

export function savingsPercent(quote, baseline) {
  if (!quote || !baseline || baseline.cents <= 0) return 0;
  return Math.max(0, Math.round((1 - (quote.cents / quote.quantity) / (baseline.cents / baseline.quantity)) * 100));
}

export function validateArtwork(file, maxBytes = 10 * 1024 * 1024) {
  if (!file) return '';
  const extension = String(file.name || '').split('.').pop().toLowerCase();
  const types = { png: ['image/png'], pdf: ['application/pdf'], ai: ['application/postscript', 'application/pdf', 'application/illustrator', 'application/octet-stream'], svg: ['image/svg+xml'] };
  if (!types[extension]) return 'Choose a PNG, PDF, AI or SVG file.';
  if (file.size <= 0) return 'This file is empty. Choose another file.';
  if (file.size > maxBytes) return `Choose a file smaller than ${Math.round(maxBytes / 1024 / 1024)} MB.`;
  if (file.type && !types[extension].includes(file.type)) return 'The file type does not match its extension.';
  return '';
}

export function addBusinessDays(start, days, holidays = []) {
  const date = new Date(start);
  date.setUTCHours(12, 0, 0, 0);
  const excluded = new Set(holidays);
  let remaining = Math.max(0, Number(days) || 0);
  while (remaining > 0) {
    date.setUTCDate(date.getUTCDate() + 1);
    if (![0, 6].includes(date.getUTCDay()) && !excluded.has(date.toISOString().slice(0, 10))) remaining--;
  }
  return date;
}

export function businessToday(now = new Date(), timeZone = 'America/New_York') {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const part = (type) => parts.find((item) => item.type === type).value;
  return new Date(`${part('year')}-${part('month')}-${part('day')}T12:00:00Z`);
}

export function addCalendarDays(start, days) {
  const date = new Date(start);
  date.setUTCHours(12, 0, 0, 0);
  date.setUTCDate(date.getUTCDate() + Math.max(0, Number(days) || 0));
  return date;
}

export function deliveryWindows(config, now = new Date()) {
  const delivery = config.delivery;
  if (!delivery?.enabled) return [];
  const start = businessToday(now, delivery.timeZone || 'America/New_York');
  const addDays = delivery.dayType === 'calendar' ? addCalendarDays : addBusinessDays;
  return delivery.methods.map((method) => ({
    ...method,
    earliest: addDays(start, delivery.production.min + method.days.min, delivery.holidays),
    latest: addDays(start, delivery.production.max + method.days.max, delivery.holidays),
  }));
}

export function validateConfig(config) {
  if (!config || config.version !== 1) throw new Error('Unsupported custom product configuration.');
  if (!/^[A-Z]{3}$/.test(config.currency || '')) throw new Error('A quote currency is required.');
  if (!config.sizes?.length || !config.lids?.some((lid) => lid.id === 'none') || !config.quotes?.length) throw new Error('Sizes, lid options and quotes are required.');
  const keys = new Set();
  for (const quote of config.quotes) {
    if (!config.sizes.some((size) => size.id === quote.size) || !config.lids.some((lid) => lid.id === quote.lid)) throw new Error('Unknown quote option.');
    if ((quote.lid === 'none' && quote.color !== 'none') || (quote.lid !== 'none' && !config.colors.some((color) => color.id === quote.color))) throw new Error('Invalid lid/color combination.');
    if (!Number.isSafeInteger(quote.quantity) || quote.quantity <= 0 || !Number.isSafeInteger(quote.cents) || quote.cents <= 0) throw new Error('Quote quantities and total prices must be positive integers.');
    const key = quoteKey(quote.size, quote.lid, quote.color, quote.quantity);
    if (keys.has(key)) throw new Error(`Duplicate quote: ${key}`);
    keys.add(key);
  }
  return config;
}

export function publishedReviews(data, { rating = 'all', sort = 'newest', limit = Infinity } = {}) {
  return (Array.isArray(data) ? data : []).filter((review) => Number.isInteger(review.rating) && review.rating >= 1 && review.rating <= 5 && typeof review.text === 'string' && review.text.trim() && typeof review.author === 'string' && review.author.trim())
    .filter((review) => rating === 'all' || review.rating === Number(rating))
    .sort((a, b) => sort === 'highest' ? b.rating - a.rating : sort === 'lowest' ? a.rating - b.rating : String(b.date || '').localeCompare(String(a.date || '')))
    .slice(0, limit);
}
